/**
 * Payroll API Client
 *
 * Client functions for interacting with the Payroll API
 */
import { useAuthStore } from '../../stores/auth.store';
import { config } from '../config';
const PAYROLL_API_URL = config.api.payroll;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
async function handleResponse(response) {
    if (!response.ok) {
        // For 404s, return a more specific error that can be detected by retry logic
        if (response.status === 404) {
            throw new Error(`HTTP 404: ${response.url}`);
        }
        // For 403s, return a more specific error with the actual message
        if (response.status === 403) {
            const error = await response.json().catch(() => ({ message: 'Forbidden' }));
            throw new Error(`HTTP 403: ${error.message || 'Forbidden'}`);
        }
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
}
/**
 * List payroll periods
 */
export async function listPayrollPeriods(count) {
    const url = new URL(`${PAYROLL_API_URL}/periods`);
    if (count) {
        url.searchParams.set('count', count.toString());
    }
    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get payroll period details
 */
export async function getPayrollPeriod(periodId) {
    const response = await fetch(`${PAYROLL_API_URL}/periods/${periodId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Approve a payroll period (admin only)
 */
export async function approvePayrollPeriod(periodId, notes) {
    const response = await fetch(`${PAYROLL_API_URL}/periods/${periodId}/approve`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes }),
    });
    return handleResponse(response);
}
/**
 * Approve an individual employee's timesheet for a period (manager or admin)
 */
export async function approveEmployeeTimesheet(periodId, employeeId) {
    const response = await fetch(`${PAYROLL_API_URL}/periods/${periodId}/employees/${employeeId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Export payroll period to Gusto CSV (admin only)
 * Downloads a CSV file compatible with Gusto Smart Import
 */
export async function exportGustoCSV(periodId) {
    const response = await fetch(`${PAYROLL_API_URL}/periods/${periodId}/export/gusto`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.blob();
}
